import styled, { keyframes, css } from 'styled-components';
import { styles } from '../constants/styles.js';
import { spacing } from '../constants/spacing.js';
import { fontFamilyStyles } from '../ContentText/styles.web.js';
import { boxStyles } from '../Box/styles.js';
import { boxStyledOptions } from '../utils/helpers/styled.js';
import { px2rem, opacify } from '../utils/helpers/style.js';
import { themeVariant } from '../utils/helpers/themeVariant.web.js';

var getTransparentColor = function getTransparentColor(kind) {
  return function (props) {
    var readOnly = props.$readOnly,
      disabledStyle = props.$disabledStyle,
      color = props.$color;
    var opacified = opacify(color, 0.4);
    if (disabledStyle) {
      switch (kind) {
        case 'fg':
        case 'label':
        case 'bg':
        case 'border':
          return opacified;
      }
    }
    if (readOnly) {
      switch (kind) {
        case 'label':
          return color;
        case 'fg':
        case 'bg':
        case 'border':
          return opacified;
      }
    }
    switch (kind) {
      case 'bg':
        return 'transparent';
      case 'fg':
      case 'border':
      case 'label':
        {
          return color;
        }
    }
  };
};
var getActiveColor = function getActiveColor(kind) {
  return function (props) {
    var readOnly = props.$readOnly,
      disabledStyle = props.$disabledStyle,
      color = props.$color,
      transparent = props.$transparent,
      theme = props.theme;
    if (transparent) {
      return getTransparentColor(kind)(props);
    }
    var checkboxTheme = theme.checkbox.web;
    if (disabledStyle) {
      switch (kind) {
        case 'fg':
          return checkboxTheme.disabled.borderColor;
        case 'bg':
          return checkboxTheme.disabled.backgroundColor;
        case 'border':
          return checkboxTheme.disabled.borderColor;
        case 'label':
          return checkboxTheme.label.disabled;
      }
    }
    if (readOnly) {
      switch (kind) {
        case 'fg':
          return checkboxTheme.background;
        case 'bg':
          return checkboxTheme.disabled.borderColor;
        case 'border':
          return checkboxTheme.disabled.borderColor;
        case 'label':
          return checkboxTheme.label.normal;
      }
    }
    switch (kind) {
      case 'fg':
        return checkboxTheme.background;
      case 'bg':
        return color;
      case 'border':
        return color;
      case 'label':
        return checkboxTheme.label.normal;
    }
  };
};
var inAnimation = /*#__PURE__*/keyframes(["0%{animation-timing-function:ease-in;opacity:0;height:0;width:0;}25%{animation-timing-function:ease-out;opacity:1;height:", ";width:0;}100%{opacity:1;height:", ";width:", ";}"], px2rem(6), px2rem(6), px2rem(11));
var outAnimation = /*#__PURE__*/keyframes(["from{opacity:1;height:", ";width:", ";}to{opacity:0;height:", ";width:", ";}"], px2rem(6), px2rem(11), px2rem(6), px2rem(11));
var field = /*#__PURE__*/styled.div.withConfig(boxStyledOptions).withConfig({
  displayName: "styles__field",
  componentId: "sc-1bvwm4b-0"
})(["", ";display:inline-block;position:relative;", ";"], fontFamilyStyles, boxStyles);
var textLabel = /*#__PURE__*/styled.label.withConfig({
  displayName: "styles__textLabel",
  componentId: "sc-1bvwm4b-1"
})(["color:", ";display:block;font-size:", ";line-height:", ";min-height:", ";padding-left:", ";user-select:none;&:empty{padding-left:", ";}"], getActiveColor('label'), themeVariant({
  normal: function normal(p) {
    return p.theme.checkbox.web.label.fontSize;
  },
  condensed: function condensed(p) {
    var _p$theme$checkbox$web;
    return (_p$theme$checkbox$web = p.theme.checkbox.web.variants.condensed) === null || _p$theme$checkbox$web === undefined || (_p$theme$checkbox$web = _p$theme$checkbox$web.label) === null || _p$theme$checkbox$web === undefined ? undefined : _p$theme$checkbox$web.fontSize;
  }
}), function (props) {
  return props.theme.checkbox.web.label.lineHeight;
}, px2rem(24), spacing.m, spacing.xs);
var icon = /*#__PURE__*/styled.span.withConfig({
  displayName: "styles__icon",
  componentId: "sc-1bvwm4b-2"
})(["position:absolute;top:", ";left:", ";height:", ";width:", ";transform:rotate(-45deg);pointer-events:none;&:after{", " animation-fill-mode:both;border-color:", ";border-width:0 0 ", " ", ";border-style:solid;box-sizing:border-box;content:'';left:0;position:absolute;top:0;}"], px2rem(9), px2rem(7), px2rem(4), px2rem(9), function (props) {
  return props.$checked ? props.$animate ? css(["animation:", " ", " forwards;"], inAnimation, styles.transition.timing.base) : css(["height:", ";width:", ";"], px2rem(6), px2rem(11)) : props.$animate ? css(["animation:", " ", " forwards;"], outAnimation, styles.transition.timing.base) : css(["opacity:0;"]);
}, getActiveColor('fg'), px2rem(2), px2rem(2));
var checkmark = /*#__PURE__*/styled.span.withConfig({
  displayName: "styles__checkmark",
  componentId: "sc-1bvwm4b-3"
})(["pointer-events:none;&:before{position:absolute;left:0;top:0;height:", ";width:", ";content:'';background:", ";border:", " solid ", ";border-radius:", ";box-sizing:border-box;transition:background ", ",border ", ";transition-timing-function:", ";}"], px2rem(24), px2rem(24), function (props) {
  return props.$transparent ? 'transparent' : props.theme.checkbox.web.background;
}, px2rem(2), function (props) {
  return props.$transparent ? props.$color : props.theme.checkbox.web.borders.color;
}, function (props) {
  return px2rem(props.theme.checkbox.web.borders.radius);
}, styles.transition.timing.base, styles.transition.timing.base, styles.transition["function"].base);

// similar one is in Radio styles
var focusAnimation = /*#__PURE__*/keyframes(["from{transform:scale(.1,.1);opacity:0;}to{transform:scale(1,1);opacity:1;}"]);
var focusIndicator = /*#__PURE__*/styled.span.withConfig({
  displayName: "styles__focusIndicator",
  componentId: "sc-1bvwm4b-4"
})(["background:", ";display:inline-block;position:absolute;top:", ";left:", ";height:", ";width:", ";border-radius:50%;pointer-events:none;opacity:0;"], function (props) {
  return opacify(props.$color, props.theme.focusIndicator.opacity);
}, px2rem(-10), px2rem(-10), px2rem(44), px2rem(44));
var input = /*#__PURE__*/styled.input.withConfig({
  displayName: "styles__input",
  componentId: "sc-1bvwm4b-5"
})(["appearance:none;background-color:transparent;position:absolute;min-width:", ";min-height:100%;margin:0;&:not(:disabled){&:checked{~ ", "{&:before{background:", ";border-color:", ";}}}&:focus-visible{outline-offset:0;box-shadow:0 0 0 4px ", ";outline:2px solid ", ";}&:focus,&:hover{cursor:pointer;~ ", "{&:before{border-color:", ";}}}&:hover{~ ", "{animation:", " ", " forwards;}}}", " ", ""], function (props) {
  return props.$hasLabel ? '100%' : px2rem(24);
}, checkmark, getActiveColor('bg'), getActiveColor('border'), function (p) {
  return p.theme.tokens.focus.primary;
}, function (p) {
  return p.theme.tokens.focus.secondary;
}, checkmark, function (props) {
  return props.$color;
}, focusIndicator, focusAnimation, styles.transition.timing.base, function (props) {
  return props.$disabledStyle && css(["~ ", "{color:", ";}~ ", "{border-color:", ";pointer-events:auto;cursor:not-allowed;&:before{background:", ";border-color:", ";}}~ ", "{&:after{border-color:", ";}}"], textLabel, getActiveColor('label'), checkmark, function (p) {
    return p.theme.checkbox.web.disabled.borderColor;
  }, props.$transparent ? 'transparent' : getActiveColor('bg')(props), getActiveColor('border'), icon, getActiveColor('fg'));
}, function (props) {
  return props.$readOnly && css(["~ ", "{pointer-events:auto;cursor:not-allowed;&:before{background:", ";border-color:", ";}}"], checkmark, props.$transparent ? 'transparent' : getActiveColor('bg')(props), getActiveColor('border'));
});

export { checkmark, field, focusIndicator, icon, input, textLabel };
